import { useEffect } from 'react';
import { useBuilderStore } from '../state/builderStore';
import { saveDraftNow } from '../state/autosave';

/**
 * Builder-scoped keyboard shortcuts (build-plan §7.6 / §5.3):
 *   Cmd/Ctrl+Z         → document undo
 *   Cmd/Ctrl+Shift+Z   → document redo
 *   Ctrl+Y             → redo (Windows convention)
 *   Cmd/Ctrl+S         → save the draft NOW (force-flush the autosave) — even from a text field
 *
 * FOCUS-AWARE (the load-bearing detail, §5.3): when focus is inside a text input, textarea, or a
 * contenteditable region (Tiptap), we DON'T hijack undo/redo — the field/rich-text editor handles its
 * OWN undo. Document-level undo operates between Tiptap commits, never on top of them. (The native
 * select listbox/segmented controls aren't editable text, so shortcuts still work there.) Save is the
 * one shortcut that fires even from a text field (it never conflicts with editing).
 */
function isEditableTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
  if (el.isContentEditable) return true;
  // A Tiptap ProseMirror surface is contenteditable; closest() catches focus on inner nodes too.
  return !!el.closest('[contenteditable="true"], .ProseMirror');
}

export function useBuilderShortcuts(): void {
  const undo = useBuilderStore((s) => s.undo);
  const redo = useBuilderStore((s) => s.redo);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const key = e.key.toLowerCase();

      // Save NOW — the browser's own Cmd/Ctrl+S "save page" dialog must be suppressed; ours wins, and
      // it fires even from a text field (the seller expects ⌘S to save their work, not the field).
      if (key === 's') {
        e.preventDefault();
        void saveDraftNow();
        return;
      }

      if (key === 'z') {
        if (isEditableTarget(e.target)) return; // let the editor own its undo/redo
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      // Ctrl+Y redo (Windows).
      if (key === 'y' && !e.metaKey) {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        redo();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);
}
